/**
 * The tenant's billing overview (ADR-0041 §11) — current plan + labelled usage meters +
 * the public plans catalog, read-only (pre-Stripe there's no self-serve plan change).
 * Feeds the **Abbonamento** settings section; the SAME shape is returned per-tenant to the
 * superadmin (`/api/platform/tenants/{id}/usage`), so the two views share `BillingOverviewView`.
 */
import { api } from "./api";

export interface BillingPrice {
  price_cents: number | null; // MONTHLY price; null = no price (custom / free)
  price_year_cents: number | null; // optional ANNUAL total; null = no annual offer
  currency: string;
}

export interface BillingPlanSummary extends BillingPrice {
  key: string;
  name: string;
  description: string | null;
  billing_period: string; // the CADENCE the tenant chose ("month" | "year")
  status?: string | null; // Stripe sub state: active | trialing | past_due | canceled
  current_period_end?: string | null; // ISO renewal date (null = comp / no Stripe period)
  cancel_at_period_end?: boolean; // true = scheduled to churn at period end (then suspend; NOT Unlimited)
  // A DOWNGRADE scheduled at period end (owner policy: upgrades immediate, downgrades at
  // the natural expiry of what was paid). Drives the "passerai a X il …" banner + Annulla.
  pending_plan_key?: string | null;
  pending_change_at?: string | null; // ISO — when the scheduled change takes effect
}

export interface BillingFeatureRow {
  key: string;
  label: string;
  description: string;
  included: boolean;
}

export interface BillingLimitRow {
  key: string;
  label: string;
  description: string;
  kind: string | null; // "count" | "volume" | null
  limit: number | null; // null = unlimited
  used: number | null; // null = not metered / unprovisioned
}

export interface BillingPlanOption extends BillingPrice {
  key: string;
  name: string;
  description: string | null;
  current: boolean;
  features: Record<string, boolean>;
  limits: Record<string, number | null>;
}

export interface BillingOverview {
  plan: BillingPlanSummary | null;
  unlimited: boolean;
  // Whether the active sub is backed by a LIVE Stripe subscription (a checkout sub). Drives
  // the renewal-copy variant; the SWITCH flow itself is gated by the preview response, not this.
  stripe_managed?: boolean;
  // Whether Stripe billing is ON server-side. Stripe ON + a NON-Stripe sub = a plan the
  // provider assigned manually: portal + self-serve switch would both 422 (the anti
  // free-upgrade guard), so the page hides them and says "gestito dal fornitore".
  stripe_enabled?: boolean;
  features: BillingFeatureRow[];
  limits: BillingLimitRow[];
  plans: BillingPlanOption[];
}

/** One line of the Stripe-computed proration preview (description + signed amount in €). */
export interface BillingPreviewLine {
  description: string;
  amount: number;
}

/**
 * The plan-change preview (ADR-0041 Fase 1). `stripe:false` = billing off (on-prem free
 * self-grant — no money dialog). `stripe:true` carries the Stripe-COMPUTED amounts (Tray does
 * zero math; VAT via Stripe Tax) shown in the confirm dialog; `proration_date` is echoed back
 * to `changePlan` so the charge == what was confirmed. A blocked tenant (billed but no Stripe
 * sub) yields a 422 `billing:no_stripe_subscription` — caught by the caller, not a preview.
 */
export interface BillingChangePreview {
  stripe: boolean;
  // DOWNGRADE: the change is DEFERRED to period end — no proration, nothing charged.
  // The dialog shows "passerai a X il <effective_at>" instead of the Stripe amounts.
  deferred?: boolean;
  effective_at?: string | null;
  plan_key?: string;
  currency?: string;
  subtotal?: number;
  tax?: number;
  total?: number;
  amount_due?: number;
  proration_date?: number;
  lines?: BillingPreviewLine[];
}

export const billingApi = {
  overview: () => api.get<BillingOverview>("/billing/overview"),
  // Preview a plan change BEFORE applying: Stripe computes the proration + VAT so the confirm
  // dialog shows exactly what will be charged. 422 `billing:no_stripe_subscription` when the
  // tenant is billed but has no Stripe sub (switch blocked → Customer Portal).
  previewChangePlan: (planKey: string, period: BillingPeriod = "month") =>
    api.post<BillingChangePreview>("/billing/change-plan/preview", {
      plan_key: planKey,
      billing_period: period,
    }),
  // Apply the switch. With a Stripe sub the proration is charged IMMEDIATELY (always_invoice),
  // pinned to the preview's `proration_date` so the charge matches the confirmed amount.
  // Billing off → immediate free self-grant. Returns the new overview.
  changePlan: (planKey: string, period: BillingPeriod = "month", prorationDate?: number) =>
    api.post<BillingOverview>("/billing/change-plan", {
      plan_key: planKey,
      billing_period: period,
      proration_date: prorationDate,
    }),
  // Open the hosted Stripe Customer Portal (ADR-0041 Fase 1) — manage card / cancel /
  // upgrade. Returns the URL to redirect into. 422 when billing is off or no customer.
  portal: () => api.post<{ url: string }>("/billing/portal", {}),
  // Self-serve CANCEL (cancel=true: the churn path — runs to period end, then suspends; NEVER
  // Unlimited) or RESUME (cancel=false) the tenant's own subscription. Returns the new overview.
  cancelSubscription: (cancel = true) => api.post<BillingOverview>("/billing/cancel", { cancel }),
  // Cancel a SCHEDULED plan change (the period-end downgrade): the sub simply continues on
  // its current plan. 422 `billing:no_pending_change` when nothing is scheduled.
  cancelPendingChange: () => api.post<BillingOverview>("/billing/change-plan/cancel-pending", {}),
  // Attach a CARD to a manually-assigned plan — the way OUT of "gestito dal fornitore".
  // Distinct from change-plan (which refuses without a Stripe sub, the anti free-upgrade
  // guard): here the tenant PAYS. Returns the Stripe Checkout URL to redirect into; the
  // subscription is linked by the webhook, not by this call. Omit `planKey` to pay for the
  // plan they're already on. 422 when billing is off or Stripe already manages them.
  activateCard: (planKey?: string, period: BillingPeriod = "month") =>
    api.post<{ url: string }>("/billing/activate-card", {
      plan_key: planKey ?? null,
      billing_period: period,
    }),
};

export type BillingPeriod = "month" | "year";

/** True when the plan offers an annual cadence (an optional second price). */
export function hasAnnual(p: BillingPrice): boolean {
  return p.price_year_cents != null;
}

/** Format a MAJOR-unit amount (€, already divided by 100 — e.g. the Stripe preview amounts).
 * Shows 2 decimals and is sign-aware so a downgrade credit reads "-€12,34". */
export function formatCurrency(value: number, currency: string): string {
  return value.toLocaleString(undefined, {
    style: "currency",
    currency: currency || "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatMoney(cents: number, currency: string): string {
  return (cents / 100).toLocaleString(undefined, {
    style: "currency",
    currency: currency || "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/**
 * Format a plan price for the chosen cadence. We ALWAYS show the **per-month** figure
 * (the owner's rule: "va mostrato il prezzo mensile anche se si acquisterà un anno"):
 * - monthly → "€99 /mese"
 * - yearly  → "€82,50 /mese" (= annual total ÷ 12) + a "fatturato annualmente" note
 * "Gratis" for 0, "—" when no monthly price is set.
 */
export function formatPrice(
  p: BillingPrice,
  t: (k: string, d: string) => string,
  period: BillingPeriod = "month",
): string {
  if (period === "year" && p.price_year_cents != null) {
    const perMonth = formatMoney(p.price_year_cents / 12, p.currency);
    return `${perMonth} ${t("billing.perMonth", "/mese")} · ${t(
      "billing.billedAnnually",
      "fatturato annualmente",
    )}`;
  }
  if (p.price_cents === null) return "—";
  if (p.price_cents === 0) return t("billing.free", "Gratis");
  return `${formatMoney(p.price_cents, p.currency)} ${t("billing.perMonth", "/mese")}`;
}

/** The per-month figure ONLY (no "fatturato annualmente" suffix) — for the big price line in a
 * plan card, where the suffix would wrap the 2xl heading. The cadence note rides a small line. */
export function formatPriceShort(
  p: BillingPrice,
  t: (k: string, d: string) => string,
  period: BillingPeriod = "month",
): string {
  if (period === "year" && p.price_year_cents != null) {
    return `${formatMoney(p.price_year_cents / 12, p.currency)} ${t("billing.perMonth", "/mese")}`;
  }
  if (p.price_cents === null) return "—";
  if (p.price_cents === 0) return t("billing.free", "Gratis");
  return `${formatMoney(p.price_cents, p.currency)} ${t("billing.perMonth", "/mese")}`;
}

/** The annual TOTAL, formatted (for the "€990/anno" line under the per-month figure). */
export function formatAnnualTotal(
  p: BillingPrice,
  t: (k: string, d: string) => string,
): string | null {
  if (p.price_year_cents == null) return null;
  return `${formatMoney(p.price_year_cents, p.currency)} ${t("billing.perYear", "/anno")}`;
}

/** Format a decimal-GB value adaptively (GB / MB / KB), so a sub-GB tenant reads as
 * "11 MB" instead of a truncated "0 GB". `null` → "—". Used for the `max_storage_gb` axis. */
export function formatGb(gb: number | null): string {
  if (gb === null) return "—";
  if (gb >= 1) return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
  const mb = gb * 1000; // decimal GB → MB (metering uses /1e9)
  if (mb >= 1) return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
  return `${Math.max(0, Math.round(gb * 1_000_000))} KB`;
}
